'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import { 
  ChevronDown, 
  Sparkles, 
  ArrowRight, 
  Play, 
  Pause,
  Rocket,
  Users,
  Star,
  Clock,
  Award,
  Shield,
  Zap,
  TrendingUp
} from 'lucide-react'
import { translations } from '@/lib/translations'
import heroVideo from '../lib/asset/portfolio/From Main Klickpin CF- Ai - mZc6g3cYr.mp4'

export default function HeroSection({ language }) {
  const t = translations[language]
  const [isVisible, setIsVisible] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [isVideoPlaying, setIsVideoPlaying] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [activeStat, setActiveStat] = useState(null)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const videoRef = useRef(null)
  const containerRef = useRef(null)
  const particlesRef = useRef(null)

  // Check if mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Optimized parallax effect - disable on mobile for performance
  useEffect(() => {
    if (isMobile) return

    const handleMouseMove = (e) => {
      if (!containerRef.current) return
      const { left, top, width, height } = containerRef.current.getBoundingClientRect()
      
      requestAnimationFrame(() => {
        const x = (e.clientX - left) / width - 0.5
        const y = (e.clientY - top) / height - 0.5
        setMousePosition({ x, y })
      })
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [isMobile])

  // Trigger entrance animations
  useEffect(() => {
    setIsVisible(true)
  }, [])

  // Video playback with intersection observer for performance
  useEffect(() => {
    if (!videoRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isVideoPlaying) {
            videoRef.current.play().catch(() => {})
          } else {
            videoRef.current.pause()
          }
        })
      },
      { threshold: 0.3 }
    )

    observer.observe(videoRef.current)

    return () => observer.disconnect()
  }, [isVideoPlaying])

  const scrollToContact = useCallback(() => {
    const element = document.getElementById('contact')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const scrollToProjects = useCallback(() => {
    const element = document.getElementById('portfolio')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const toggleVideo = useCallback(() => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play().catch(error => {
          console.log('Video playback failed:', error)
          setVideoError(true)
        })
      }
      setIsVideoPlaying(!isVideoPlaying)
    }
  }, [isVideoPlaying])

  const handleVideoError = useCallback(() => {
    setVideoError(true)
    console.log('Video failed to load')
  }, [])

  // Stats data with Lucide React icons
  const stats = [
    { 
      value: '50+', 
      label: 'Projects Completed', 
      icon: Rocket,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10'
    },
    { 
      value: '50+', 
      label: 'Happy Clients', 
      icon: Users,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10'
    },
    { 
      value: '98%', 
      label: 'Client Satisfaction', 
      icon: Star,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10'
    },
    { 
      value: '24/7', 
      label: 'Support Available', 
      icon: Clock,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10'
    }
  ]

  // Additional achievement badges
  const achievements = [
   
    { icon: Shield, label: '100% Secure', color: 'text-emerald-400' },
    { icon: Zap, label: 'Fast Delivery', color: 'text-sky-400' },
    { icon: TrendingUp, label: 'Growth Guaranteed', color: 'text-rose-400' }
  ]

  return (
    <section
      ref={containerRef}
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label="Hero section"
    >
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full bg-gray-900">
        {/* Loading skeleton */}
        {!videoLoaded && !videoError && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-900 animate-pulse" />
        )}

        {/* Video element */}
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transform: !isMobile ? `translate(${mousePosition.x * 10}px, ${mousePosition.y * 10}px) scale(1.05)` : 'scale(1.05)',
            transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1), opacity 1s ease',
            willChange: 'transform'
          }}
          onLoadedData={() => setVideoLoaded(true)}
          onError={handleVideoError}
        >
          <source src={heroVideo.src || heroVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Fallback background if video fails */}
        {videoError && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-gray-800" />
        )}
      </div>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70">
        <div className="absolute inset-0 opacity-20 md:opacity-30">
          <div className="absolute top-0 -left-4 w-48 h-48 md:w-72 md:h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-48 h-48 md:w-72 md:h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-48 h-48 md:w-72 md:h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Animated particles */}
      <div ref={particlesRef} className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(isMobile ? 8 : 20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-0.5 md:w-1 md:h-1 bg-white rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: 0.2 + Math.random() * 0.3
            }}
            aria-hidden="true"
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center">
         

          {/* Achievement badges */}
          <div className="flex flex-wrap justify-center gap-2 mt-16 mb-4 md:mb-6">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon
              return (
                <div
                  key={index}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/30 backdrop-blur-sm border border-white/10 transform transition-all duration-300 hover:scale-105 hover:bg-black/40 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <Icon className={`w-3 h-3 md:w-3.5 md:h-3.5 ${achievement.color}`} />
                  <span className="text-[10px] md:text-xs text-white/90">{achievement.label}</span>
                </div>
              )
            })}
          </div>

          {/* Premium badge */}
          <div 
            className={`inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-black/30 backdrop-blur-md border border-white/20 mb-4 md:mb-6 transform transition-all duration-1000 hover:bg-black/40 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-yellow-400 animate-pulse" />
            <span className="text-xs md:text-sm font-medium text-white">Welcome to Excellence</span>
            <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-yellow-400 animate-pulse" />
          </div>

          {/* Main title */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 transform transition-all duration-1000 delay-200 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <span className="bg-gradient-to-r from-white via-yellow-200 to-white bg-clip-text text-transparent bg-300% animate-gradient">
              {t.hero.title}
            </span>
          </h1>

          {/* Subtitle */}
          <p 
            className={`text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 max-w-2xl lg:max-w-3xl mx-auto text-white/90 leading-relaxed px-2 transform transition-all duration-1000 delay-400 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {t.hero.subtitle}
          </p>

          {/* CTA Buttons */}
          <div 
            className={`flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center transform transition-all duration-1000 delay-600 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <button
              onClick={scrollToContact}
              className="group relative w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-primary to-primary/80 text-white rounded-full font-semibold overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-105 active:scale-95"
              aria-label="Get started"
            >
              <span className="relative z-10 flex items-center justify-center gap-2 text-sm md:text-base">
                {t.hero.cta}
                <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>

            <button
              onClick={scrollToProjects}
              className="group w-full sm:w-auto px-6 md:px-8 py-3 md:py-4 bg-black/30 backdrop-blur-md border border-white/30 text-white rounded-full font-semibold hover:bg-black/50 transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2 text-sm md:text-base"
              aria-label="View projects"
            >
              <Play className="w-3 h-3 md:w-4 md:h-4" />
              <span>View Projects</span>
            </button>
          </div>

          {/* Stats */}
          <div 
            className={`grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 mt-10 md:mt-16 transform transition-all duration-1000 delay-800 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <div 
                  key={index} 
                  className="group/stat relative text-center p-3 md:p-4 rounded-2xl backdrop-blur-sm bg-black/30 hover:bg-black/40 transition-all duration-500 cursor-default overflow-hidden border border-white/5"
                  onMouseEnter={() => setActiveStat(index)}
                  onMouseLeave={() => setActiveStat(null)}
                  role="status"
                  aria-label={`${stat.value} ${stat.label}`}
                >
                  {/* Animated background effect */}
                  <div className={`absolute inset-0 opacity-0 group-hover/stat:opacity-100 transition-opacity duration-500 ${stat.bgColor}`} />
                  
                  {/* Icon with animation */}
                  <div className="relative z-10 mb-2 flex justify-center">
                    <div className={`p-2 rounded-xl ${stat.bgColor} group-hover/stat:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 ${stat.color} group-hover/stat:rotate-12 transition-transform duration-300`} />
                    </div>
                  </div>
                  
                  {/* Value */}
                  <div className="relative z-10">
                    <div className="text-lg md:text-xl lg:text-2xl font-bold text-white mb-0.5 md:mb-1 transition-all duration-300 group-hover/stat:scale-110">
                      {stat.value}
                    </div>
                    <div className="text-[10px] md:text-xs lg:text-sm text-white/80 flex items-center justify-center gap-1">
                      {stat.label}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

   

      <style jsx>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(20px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-15px, 15px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        
        @media (prefers-reduced-motion: reduce) {
          .animate-blob,
          .animate-float,
          .animate-gradient,
          .animate-bounce,
          .animate-ping,
          .animate-pulse {
            animation: none;
          }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          25% {
            transform: translateY(-15px) translateX(8px);
          }
          50% {
            transform: translateY(-8px) translateX(-8px);
          }
          75% {
            transform: translateY(8px) translateX(4px);
          }
        }
        
        .animate-float {
          animation: float linear infinite;
        }
        
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .bg-300\% {
          background-size: 300% 300%;
        }
        
        .animate-gradient {
          animation: gradient 8s ease infinite;
        }
      `}</style>
    </section>
  )
}